/**
 * @file Main application component
 * @description 
 * The root component that sets up routing, global providers, and layout structure.
 * It wraps all routes with the necessary providers and global UI elements.
 * 
 * Key features:
 * - Initial loading screen with guaranteed minimum display time
 * - Content is only rendered after loading is complete
 * - Consistent layout with Navbar and Footer on all pages
 * - React Query integration for data fetching
 * - Routing configuration using Wouter
 * - Toast notification system
 * - Code splitting with React.lazy and Suspense for improved performance
 * - Progressive loading of route components
 * - Font loading optimization
 */

import { useState, useEffect, lazy, Suspense } from "react";
import { Switch, Route } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import LoadingScreen from "@/components/LoadingScreen";
import { initFontLoading } from "@/lib/font-loading";
import { UNDER_CONSTRUCTION } from "@/config/site.config";

// Initialize font loading optimization as early as possible
initFontLoading();

// Lazy load all page components for code splitting
// This reduces the initial bundle size by only loading components when needed
const Home = lazy(() => import("@/pages/home"));
const About = lazy(() => import("@/pages/about"));
const Services = lazy(() => import("@/pages/services"));
const Work = lazy(() => import("@/pages/work"));
const ProjectDetail = lazy(() => import("@/pages/project-detail"));
const NotFound = lazy(() => import("@/pages/not-found"));
const HomeUnderConstruction = lazy(() => import("@/pages/home-under-construction"));

// Create a simple route loading component for Suspense fallback
const RouteLoadingFallback = () => (
  <div className="min-h-screen w-full flex items-center justify-center bg-[var(--ivory)]">
    <div className="text-[var(--red)] tracking-wider font-raleway">Loading page...</div>
  </div>
);

// Track initial page load using localStorage to persist across refreshes
// This approach works better than a variable which gets reset on page refresh
const hasInitiallyLoaded = localStorage.getItem('hasInitiallyLoaded') === 'true';

/**
 * Router component that handles route configuration with code splitting
 * Uses Suspense to show a fallback while route components are loading
 */
function Router() {
  // Add event listener for navigation to preload resources
  useEffect(() => {
    // Function to preload resources based on the current route
    const preloadResourcesForRoute = (path: string) => {
      // Skip for external links
      if (path.startsWith('http') || path.startsWith('mailto:')) return;
      
      // Preload strategy based on path
      if (path === '/') {
        // Preload home page resources
        const homeImage = document.createElement('link');
        homeImage.rel = 'preload';
        homeImage.href = '/logo.png';
        homeImage.as = 'image';
        document.head.appendChild(homeImage);
      } else if (path === '/work') {
        // Preload work page resources
        ['work_steengoed_2.jpeg', 'work_nomet_1.jpeg'].forEach(image => {
          const imgLink = document.createElement('link');
          imgLink.rel = 'preload';
          imgLink.href = `/${image}`;
          imgLink.as = 'image';
          document.head.appendChild(imgLink);
        });
      } else if (path.startsWith('/work/')) {
        // Preload project detail resources
        const projectSlug = path.split('/').pop();
        if (projectSlug) {
          const imgLink = document.createElement('link');
          imgLink.rel = 'preload';
          imgLink.href = `/work_${projectSlug.replace(/-/g, '_')}_1.jpeg`;
          imgLink.as = 'image';
          document.head.appendChild(imgLink);
        }
      } else if (path === '/services') {
        // Preload services page resources
        const servicesImage = document.createElement('link');
        servicesImage.rel = 'preload';
        servicesImage.href = '/services_header.png';
        servicesImage.as = 'image';
        document.head.appendChild(servicesImage);
      } else if (path === '/about') {
        // Preload about page resources
        const aboutImage = document.createElement('link');
        aboutImage.rel = 'preload';
        aboutImage.href = '/about_header_2.png';
        aboutImage.as = 'image';
        document.head.appendChild(aboutImage);
      }
    };
    
    // Preload resources for initial route
    preloadResourcesForRoute(window.location.pathname);
    
    // Add event listener for navigation events
    const handleNavigation = () => {
      preloadResourcesForRoute(window.location.pathname);
    };
    
    window.addEventListener('popstate', handleNavigation);
    
    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, []);
  
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      {UNDER_CONSTRUCTION ? (
        // Under Construction mode: Only homepage is accessible
        <Switch>
          <Route path="/" component={HomeUnderConstruction} />
          {/* All other routes redirect to under construction homepage */}
          <Route component={HomeUnderConstruction} />
        </Switch>
      ) : (
        // Normal mode: All routes are accessible
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/about" component={About} />
          <Route path="/services" component={Services} />
          <Route path="/work" component={Work} />
          <Route path="/work/:slug" component={ProjectDetail} />
          <Route component={NotFound} />
        </Switch>
      )}
    </Suspense>
  );
}

/**
 * Main App component that sets up the application structure
 * Includes initial loading screen, global providers, and layout
 */
function App() {
  // State to track if the app is ready to display
  const [appReady, setAppReady] = useState(hasInitiallyLoaded);
  const [showLoadingScreen, setShowLoadingScreen] = useState(!hasInitiallyLoaded);
  
  useEffect(() => {
    // If already loaded once, no need to show loading screen
    if (hasInitiallyLoaded) {
      return;
    }

    // Set a minimum display time for the loading screen
    const timer = setTimeout(() => {
      setAppReady(true);
      // Mark as initially loaded in localStorage
      localStorage.setItem('hasInitiallyLoaded', 'true');
      
      // Add a small delay before hiding the loading screen to allow for exit animation
      setTimeout(() => {
        setShowLoadingScreen(false);
      }, 800); // Match the exit animation duration
    }, 3000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Handle loading complete callback from LoadingScreen
  const handleLoadingComplete = () => {
    setAppReady(true);
    localStorage.setItem('hasInitiallyLoaded', 'true');
  };

  return (
    <QueryClientProvider client={queryClient}>
      {/* Show loading screen only on first load */}
      {showLoadingScreen && (
        <LoadingScreen 
          minDisplayTime={3000} 
          onLoadingComplete={handleLoadingComplete} 
        />
      )}
      
      {/* Always render the main app content, but it will be hidden behind the loading screen if not ready */}
      <div className={`flex flex-col min-h-screen ${!appReady ? 'opacity-0' : 'opacity-100 transition-opacity duration-500'}`}>
        {/* Hide Navbar when in under construction mode */}
        {!UNDER_CONSTRUCTION && <Navbar />}
        <div className="flex-grow">
          <Router />
        </div>
        {/* Hide Footer when in under construction mode */}
        {!UNDER_CONSTRUCTION && <Footer />}
      </div>
      
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;